import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import {
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import ErrorBox from "../Shared/ErrorBox";
import { useTheme } from "@mui/material/styles";
import { useAvailableItemConfigContext } from "../../hooks/useContext/useAvailableItemConfigContext";
import useFetchOpenStatus from "../../hooks/useFetchOpenStatus";
import { useOrderContext } from "../../hooks/useContext/useOrderContext";
import validator from 'validator';
import CircularProgress from '@mui/material/CircularProgress';

export default function CheckoutForm({ order, customerInfo, paymentMethod, setEmptyFields, setError, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const { dispatch } = useOrderContext();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emptyFieldError, setEmptyFieldError] = useState(null);
  const theme = useTheme();
  const { openStatus } = useAvailableItemConfigContext();
  const storeClosedText = "Przepraszamy, obecnie jesteśmy zamknięci!";

  useFetchOpenStatus();

  //email name error handing
  const validateFields = (customerInfo) => {
    const emptyFieldsList = [];

    if (!customerInfo.firstName) {
      emptyFieldsList.push("firstName");
      setError("Proszę wprowadzić imię");
      setEmptyFieldError("Proszę wprowadzić imię");
    }
    if (!customerInfo.lastName) {
      emptyFieldsList.push("lastName");
      setError("Proszę wprowadzić nazwisko");
      setEmptyFieldError("Proszę wprowadzić nazwisko");
    }
    if (!customerInfo.email) {
      emptyFieldsList.push("email");
      setError("Proszę wprowadzić adres e-mail");
      setEmptyFieldError("Proszę wprowadzić adres e-mail");
    }
    if (customerInfo.email && !validator.isEmail(customerInfo.email)) {
      emptyFieldsList.push("email");
      setEmptyFieldError("Nieprawidłowy adres e-mail");
    }
    if (emptyFieldsList.length > 0) {
      setEmptyFields(emptyFieldsList);
      
      if (emptyFieldsList.length > 1) {
        setError("Proszę wypełnić wszystkie pola");
        setEmptyFieldError("Proszę wypełnić wszystkie pola");
      }
      // console.log('multiple emptyFieldsList: ' + emptyFieldsList);
      return emptyFieldsList;
    }

    // console.log('no errors');
    setEmptyFields([]);
    setError(null);
    setEmptyFieldError(null);
    return null;
  };


  // Function to send the order to the backend before proceeding with Stripe payment
  const sendOrderToBackend = async () => {
    const onlineOrderData = {
      name: customerInfo.firstName + ' ' + customerInfo.lastName,
      email: customerInfo.email,
      clientSecret: clientSecret,
      paymentMethod: paymentMethod === 'online',
      orderItems: order.map(item => ({
        itemName: item.itemName,
        itemQuantity: item.itemQuantity,
        itemPrice: item.itemPrice,
        itemSize: item.itemSize,
        itemType: item.itemType,
        specialInstructions: item.specialInstructions || ''
      })),
      totalPrice: order.reduce((acc, item) => acc + (item.itemQuantity * item.itemPrice), 0),
    };

    dispatch({type: 'SET_USER_INFO', payload: {name: onlineOrderData.name, email: onlineOrderData.email}});

    try {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/createNewPaidOrder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(onlineOrderData),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        return { error: data.error || 'Error placing order' };
      }

      return { orderId: data._id };

    } catch (error) {
      setMessage(error.message);
      setIsLoading(false);
      return {error: error.message};
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setIsLoading(true);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    if (validateFields(customerInfo)) {
      setIsLoading(false);
      return;
    }

    const response = await sendOrderToBackend({order});
    if (response.error) {
      setIsLoading(false);
      setMessage(response.error)
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: process.env.REACT_APP_CLIENT_URL + "/complete",
      }},
    );
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occurred.");
    }
    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "accordion"
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%'
    }}>

      <PaymentElement id="payment-element" options={paymentElementOptions} />
      {!(!stripe || !elements) && (
        <>
        {!openStatus && paymentMethod === 'online' && <ErrorBox error={storeClosedText} color={"error"} />}
        {message && emptyFieldError && <ErrorBox id="payment-message" error={emptyFieldError + '   &   ' + message} color={theme.palette.common.error} />}
        {message && !emptyFieldError && <ErrorBox id="payment-message" error={message} color={theme.palette.common.error} />}
        {emptyFieldError && !message && <ErrorBox id="empty-field-message" error={emptyFieldError} color={theme.palette.common.error} />}
        {isLoading && 
          <Box sx={{ padding: '20px 0 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <CircularProgress/>
          </Box>
        }
        <Button disabled={isLoading || !stripe || !elements || !openStatus} variant="primaryButton" sx={{borderRadius: "10px", width: '100%', marginTop: '20px' }} onClick={handleSubmit}>
          {"Zatwierdź Płatność"}
        </Button>
        </>
      )}

    </Box>
  );
}
